import { getApiError } from '../../utils/errorHelper';
import React, { useState, useEffect } from 'react';
import { Building2, Users, MapPin, Phone, Mail, ChevronRight, ChevronDown, Search, ArrowLeft, Pencil, X, Save, Loader2, PieChart, Calendar, FileText, Clock, Package, Plus, Key, CheckCircle2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api, { adminApi, productsApi, companiesApi } from '../../services/api';
import { entityConfig } from '../../utils/entityTypeConfig';
import GstRegistrationSection from './gst/GstRegistrationSection';
import { KYCDocuments } from '../KYCDocuments';
import ExternalCredentials from '../ExternalCredentials';
import { toIST } from '../../utils/dateUtils';
import { INDIAN_STATES_AND_UTS } from '../../constants/indianStates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Compute service status from expiry date — NEVER use stored status
const computeServiceStatus = (expiryDate) => {
  if (!expiryDate) return { status: 'unknown', label: 'Not Set', color: 'bg-slate-500/20 admin-text-dim' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  expiry.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { status: 'expired', label: 'Expired', color: 'bg-red-500/20 text-red-400', days: diffDays };
  if (diffDays <= 30) return { status: 'expiring_soon', label: 'Expiring Soon', color: 'bg-amber-500/20 text-amber-400', days: diffDays };
  if (diffDays <= 60) return { status: 'due_soon', label: 'Due Soon', color: 'bg-yellow-500/20 text-yellow-400', days: diffDays };
  return { status: 'active', label: 'Active', color: 'bg-emerald-500/20 text-emerald-400', days: diffDays };
};

// Status Badge Component - Reusable
const StatusBadge = ({ status, type }) => {
  const configs = {
    entity_type: {
      private_limited: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Private Limited' },
      llp: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'LLP' },
      opc: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'OPC' },
      partnership: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Partnership' },
      proprietorship: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Proprietorship' },
      corporation: { bg: 'bg-indigo-500/20', text: 'admin-text-green', label: 'Corporation' },
      section_8: { bg: 'bg-pink-500/20', text: 'text-pink-400', label: 'Section 8' },
      non_profit: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', label: 'Non Profit' },
      society_registered: { bg: 'bg-teal-500/20', text: 'text-teal-400', label: 'Society Registered' },
      llc: { bg: 'bg-violet-500/20', text: 'text-violet-400', label: 'LLC' },
      // US Entity Types
      us_llc: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'LLC (US)' },
      us_c_corp: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'C-Corp (US)' },
      us_s_corp: { bg: 'bg-indigo-500/20', text: 'admin-text-green', label: 'S-Corp (US)' },
      us_sole_proprietorship: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Sole Prop (US)' },
      us_partnership: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Partnership (US)' },
      us_non_profit_501c3: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', label: '501(c)(3) (US)' },
    },
    din: {
      active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Active' },
      not_applied: { bg: 'bg-slate-500/20', text: 'admin-text-dim', label: 'Not Applied' },
      deactivated: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Deactivated' },
    },
    dsc: {
      active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Active' },
      applied: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Applied' },
      not_applied: { bg: 'bg-slate-500/20', text: 'admin-text-dim', label: 'Not Applied' },
      expired: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Expired' },
    },
  };
  
  const config = configs[type]?.[String(status)] || { bg: 'bg-slate-500/20', text: 'admin-text-dim', label: status?.replace(/_/g, ' ') || 'N/A' };
  
  return (
    <span className={`text-xs px-2 py-1 rounded-full ${config.bg} ${config.text} capitalize`}>
      {config.label}
    </span>
  );
};

// Display Field Component - Shows value or hides if empty
const DisplayField = ({ label, value, mono = false, hideIfEmpty = true, prefix = '' }) => {
  if (hideIfEmpty && !value) return null;
  
  return (
    <div className="admin-field p-3">
      <p className="text-xs admin-text-soft mb-1">{label}</p>
      <p className={`text-[var(--admin-text)] text-sm ${mono ? 'font-mono' : ''}`}>{prefix}{value || 'Not provided'}</p>
    </div>
  );
};

// Summary Panel for Edit View
const SummaryPanel = ({ directorsCount = 0, shareholdersCount = 0, documentsCount = 0 }) => (
  <div className="admin-field p-4 mb-4 grid grid-cols-3 gap-4 border border-[var(--admin-border)]">
    <div className="text-center">
      <p className="text-2xl font-bold text-emerald-400">{directorsCount}</p>
      <p className="text-xs admin-text-soft">Directors</p>
    </div>
    <div className="text-center border-x border-[var(--admin-border)]">
      <p className="text-2xl font-bold text-purple-400">{shareholdersCount}</p>
      <p className="text-xs admin-text-soft">Shareholders</p>
    </div>
    <div className="text-center">
      <p className="text-2xl font-bold text-blue-400">{documentsCount}</p>
      <p className="text-xs admin-text-soft">Documents</p>
    </div>
  </div>
);

// Last Updated Badge Component
const LastUpdatedBadge = ({ updatedAt, updatedByName, updatedByRole }) => {
  if (!updatedAt) return null;
  
  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  
  const getRoleBadge = (role) => {
    if (role === 'super_admin' || role === 'admin') {
      return { text: 'Admin', color: 'bg-purple-500/20 text-purple-400' };
    }
    return { text: 'Client', color: 'bg-blue-500/20 text-blue-400' };
  };
  
  const roleBadge = getRoleBadge(updatedByRole);
  
  return (
    <div className="flex items-center gap-2 text-xs admin-text-soft admin-field px-3 py-2 mt-4">
      <Clock className="w-3.5 h-3.5" />
      <span>Last updated {formatDate(updatedAt)}</span>
      {updatedByName && (
        <>
          <span>by</span>
          <span className="font-medium admin-text-dim">{updatedByName}</span>
          <span className={`px-2 py-0.5 rounded-full text-xs ${roleBadge.color}`}>
            {roleBadge.text}
          </span>
        </>
      )}
    </div>
  );
};

// India Entity Types
const INDIA_ENTITY_TYPES = [
  { value: 'private_limited', label: 'Private Limited' },
  { value: 'opc', label: 'OPC' },
  { value: 'llp', label: 'LLP' },
  { value: 'llc', label: 'LLC' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'proprietorship', label: 'Proprietorship' },
  { value: 'corporation', label: 'Corporation' },
  { value: 'section_8', label: 'Section 8' },
  { value: 'non_profit', label: 'Non Profit' },
  { value: 'society_registered', label: 'Society Registered' },
];

// US Entity Types
const US_ENTITY_TYPES = [
  { value: 'us_llc', label: 'LLC' },
  { value: 'us_c_corp', label: 'C-Corporation' },
  { value: 'us_s_corp', label: 'S-Corporation' },
  { value: 'us_sole_proprietorship', label: 'Sole Proprietorship' },
  { value: 'us_partnership', label: 'Partnership' },
  { value: 'us_non_profit_501c3', label: '501(c)(3) Non-Profit' },
];

// Combined for backward compatibility
const ENTITY_TYPES = [...INDIA_ENTITY_TYPES, ...US_ENTITY_TYPES];

// Indian States + Union Territories (single source of truth)
const INDIA_STATES = INDIAN_STATES_AND_UTS;

// US States
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];

// Backward compatibility
const STATES = INDIA_STATES;

// Helper to check if company is US-based
const isUSCompany = (company) => company?.country === 'US' || company?.entity_type?.startsWith('us_');

// Helper to get US state name from code
const getUSStateName = (code) => US_STATES.find(s => s.code === code)?.name || code;

const DSC_OPTIONS = [
  { value: 'not_applied', label: 'Not Applied' },
  { value: 'applied', label: 'Applied' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
];

const DIN_STATUS_OPTIONS = [
  { value: 'not_applied', label: 'Not Applied' },
  { value: 'active', label: 'Active' },
  { value: 'deactivated', label: 'De-Activated' },
];

const CompanyCard = ({ company, onClick, isSelected }) => {
  const entityColors = {
    private_limited: 'bg-blue-500/20 text-blue-400',
    llp: 'bg-purple-500/20 text-purple-400',
    opc: 'bg-emerald-500/20 text-emerald-400',
    partnership: 'bg-amber-500/20 text-amber-400',
    corporation: 'bg-indigo-500/20 admin-text-green',
    section_8: 'bg-pink-500/20 text-pink-400',
    non_profit: 'bg-cyan-500/20 text-cyan-400',
    society_registered: 'bg-teal-500/20 text-teal-400',
    // US entity types
    us_llc: 'bg-blue-500/20 text-blue-400',
    us_c_corp: 'bg-purple-500/20 text-purple-400',
    us_s_corp: 'bg-indigo-500/20 admin-text-green',
    us_sole_proprietorship: 'bg-amber-500/20 text-amber-400',
    us_partnership: 'bg-orange-500/20 text-orange-400',
    us_non_profit_501c3: 'bg-cyan-500/20 text-cyan-400',
  };

  const isUS = isUSCompany(company);
  const getEntityLabel = () => {
    if (isUS) {
      const type = US_ENTITY_TYPES.find(t => t.value === company.entity_type);
      return type?.label || company.entity_type?.replace('us_', '').replace(/_/g, ' ');
    }
    return company.entity_type?.replace('_', ' ') || 'Private Limited';
  };

  return (
    <div 
      onClick={onClick}
      className={`admin-bg-card-inner border rounded-xl p-5 cursor-pointer transition-all duration-300 hover:border-[var(--admin-green)]/50 hover:bg-[var(--admin-bg-elevated)] ${
        isSelected ? 'border-[var(--admin-green)] ring-2 ' : 'border-[var(--admin-border)]'
      }`}
      data-testid={`company-card-${company.id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-[var(--admin-text)] font-bold flex-shrink-0 ${
          isUS ? 'bg-gradient-to-br from-red-500 to-blue-600' : 'bg-gradient-to-br from-blue-500 to-cyan-500'
        }`}>
          {company.company_name?.charAt(0)?.toUpperCase() || 'C'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-[var(--admin-text)] font-semibold truncate">{company.company_name}</h3>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${entityColors[company.entity_type] || entityColors.private_limited}`}>
            {getEntityLabel()}
          </span>
          {company.cin_llpin && <p className="admin-text-soft text-xs mt-2 font-mono">CIN: {company.cin_llpin}</p>}
        </div>
        <ChevronRight className="w-5 h-5 admin-text-soft" />
      </div>

      <div className="mt-4 pt-4 border-t border-[var(--admin-border)] grid grid-cols-2 gap-4 text-sm">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 admin-text-soft" />
          <span className="admin-text-dim">{company.directors_count || 0} {isUS ? 'Founders' : 'Directors'}</span>
        </div>
        <div className="flex items-center gap-2">
          <PieChart className="w-4 h-4 admin-text-soft" />
          <span className="admin-text-dim">{company.shareholders_count || 0} {isUS ? 'Members' : 'Shareholders'}</span>
        </div>
      </div>
    </div>
  );
};

const DirectorCard = ({ director, isFounder, onEdit, isUSCompany = false, showProfitShare = false, onClick }) => (
  <div 
    className={`admin-field p-4 border border-[var(--admin-border)] ${onClick ? 'cursor-pointer hover:bg-[var(--admin-bg-elevated)] hover:border-[var(--admin-green)]/30 transition-colors' : ''}`}
    onClick={onClick}
    data-testid={`director-card-${director.id || 'unknown'}`}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-[var(--admin-text)] font-semibold text-sm ${
          isUSCompany ? 'bg-gradient-to-br from-red-400 to-blue-500' : 'bg-gradient-to-br from-emerald-400 to-teal-500'
        }`}>
          {director.full_name?.charAt(0)?.toUpperCase() || 'D'}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[var(--admin-text)] font-medium">{director.full_name}</p>
            {isFounder && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
                {isUSCompany ? 'Primary Owner' : 'Founder'}
              </span>
            )}
            {director.is_authorized_signatory && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">Signatory</span>
            )}
          </div>
          <p className="admin-text-soft text-sm">{director.designation || (isUSCompany ? 'Member/Manager' : 'Director')}</p>
        </div>
      </div>
    </div>
    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
      <div className="flex items-center gap-1 admin-text-dim">
        <Mail className="w-3 h-3" />
        <span className="truncate">{director.email}</span>
      </div>
      {/* US-specific fields */}
      {isUSCompany ? (
        <>
          {director.ssn && (
            <div className="admin-text-soft">
              <span className="font-mono">SSN: ***-**-{director.ssn?.slice(-4) || '****'}</span>
            </div>
          )}
          {director.itin && (
            <div className="admin-text-soft">
              <span className="font-mono">ITIN: {director.itin}</span>
            </div>
          )}
          {director.ownership_percentage && (
            <div className="admin-text-dim">
              Ownership: <span className="text-emerald-400 font-medium">{director.ownership_percentage}%</span>
            </div>
          )}
        </>
      ) : (
        <>
          {/* India-specific fields */}
          {director.din_number && (
            <div className="flex items-center gap-2">
              <span className="admin-text-soft font-mono">DIN: {director.din_number}</span>
              <StatusBadge status={director.din_status} type="din" />
            </div>
          )}
          {director.pan_number && (
            <div className="admin-text-soft">
              <span className="font-mono">PAN: {director.pan_number}</span>
            </div>
          )}
          {director.dsc_status && director.dsc_status !== 'not_applied' && (
            <div className="flex items-center gap-1">
              <span className="admin-text-soft">DSC:</span>
              <StatusBadge status={director.dsc_status} type="dsc" />
            </div>
          )}
          {showProfitShare && director.profit_share_ratio != null && (
            <div
              className="col-span-2 mt-1 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-[11px] text-emerald-300 w-fit"
              data-testid={`director-profit-share-badge-${director.id || 'unknown'}`}
            >
              <span className="font-medium">P&amp;L Share:</span>
              <span className="font-bold">{director.profit_share_ratio}%</span>
            </div>
          )}
        </>
      )}
    </div>
  </div>
);

const ShareholderCard = ({ shareholder, onClick }) => {
  const typeColors = {
    individual: 'bg-blue-500/20 text-blue-400',
    company: 'bg-purple-500/20 text-purple-400',
    llp: 'bg-emerald-500/20 text-emerald-400',
    trust: 'bg-amber-500/20 text-amber-400',
  };

  return (
    <div 
      className={`admin-field p-4 border border-[var(--admin-border)] ${onClick ? 'cursor-pointer hover:bg-[var(--admin-bg-elevated)] hover:border-[var(--admin-green)]/30 transition-colors' : ''}`}
      onClick={onClick}
      data-testid={`shareholder-card-${shareholder.id || 'unknown'}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-[var(--admin-text)] font-semibold text-sm">
            {shareholder.shareholder_name?.charAt(0)?.toUpperCase() || 'S'}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[var(--admin-text)] font-medium">{shareholder.shareholder_name}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${typeColors[shareholder.shareholder_type] || typeColors.individual}`}>
                {shareholder.shareholder_type || 'Individual'}
              </span>
              {shareholder.is_authorized_signatory && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">Signatory</span>
              )}
            </div>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[var(--admin-text)] font-semibold">{shareholder.percent_holding?.toFixed(1)}%</p>
          <p className="admin-text-soft text-xs">{shareholder.number_of_shares?.toLocaleString()} shares</p>
        </div>
      </div>
      {(shareholder.pan_number || shareholder.shareholder_email || shareholder.din_number) && (
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          {shareholder.shareholder_email && (
            <div className="flex items-center gap-1 admin-text-dim">
              <Mail className="w-3 h-3" />
              <span className="truncate">{shareholder.shareholder_email}</span>
            </div>
          )}
          {shareholder.pan_number && (
            <div className="admin-text-soft">
              <span className="font-mono">PAN: {shareholder.pan_number}</span>
            </div>
          )}
          {shareholder.din_number && (
            <div className="flex items-center gap-2">
              <span className="admin-text-soft font-mono">DIN: {shareholder.din_number}</span>
              <StatusBadge status={shareholder.din_status} type="din" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CompanyDetailPanel = ({ company, directors, shareholders, orders, owner, onSelectDirector, onSelectShareholder, onSelectOrder, onSelectOwner, onBack, onRefresh }) => {
  const cfg = entityConfig(company?.entity_type);
  const [shAdding, setShAdding] = useState(false);
  const [shPrefill, setShPrefill] = useState(null); // pre-fill from a director if user clicked "Add as Shareholder"
  const [shSaving, setShSaving] = useState(false);

  const openShModal = (prefill = null) => {
    setShPrefill(prefill);
    setShAdding(true);
  };

  const submitShareholder = async (payload) => {
    setShSaving(true);
    try {
      await companiesApi.addShareholder(company.id, payload);
      toast.success(`${cfg.shareholder_label} added`);
      setShAdding(false);
      setShPrefill(null);
      if (onRefresh) await onRefresh();
    } catch (e) {
      toast.error(getApiError(e, `Failed to add ${cfg.shareholder_label.toLowerCase()}`));
    } finally {
      setShSaving(false);
    }
  };
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [recommending, setRecommending] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [activeServices, setActiveServices] = useState({});
  const [gstTdsExpanded, setGstTdsExpanded] = useState(false);
  const [savingGstTds, setSavingGstTds] = useState(false);
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [switchReason, setSwitchReason] = useState('');
  const [switchingBilling, setSwitchingBilling] = useState(false);
  const [gstinBreakdown, setGstinBreakdown] = useState(null);
  const [gstTdsForm, setGstTdsForm] = useState({});

  // Fetch active service flags when company changes
  useEffect(() => {
    if (company?.id && isUSCompany(company)) {
      adminApi.getCompanyActiveServices(company.id)
        .then(data => setActiveServices(data || {}))
        .catch(() => setActiveServices({}));
    } else {
      setActiveServices({});
    }
  }, [company?.id]);

  // Fetch per-GSTIN breakdown for annual billing companies
  useEffect(() => {
    if (company?.id && company?.billing_model === 'annual') {
      api.get(`/admin/companies/${company.id}/gstin-breakdown`)
        .then(res => setGstinBreakdown(res.data))
        .catch(() => setGstinBreakdown(null));
    } else {
      setGstinBreakdown(null);
    }
  }, [company?.id, company?.billing_model]);


  // Load products when modal opens
  useEffect(() => {
    if (showProductModal) {
      loadProducts();
    }
  }, [showProductModal]);

  const loadProducts = async () => {
    setLoadingProducts(true);
    try {
      const data = await productsApi.getAllAdmin();
      setProducts(data || []);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProductIds(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleRecommendProduct = async () => {
    if (selectedProductIds.length === 0) {
      toast.error('Please select at least one product');
      return;
    }
    
    setRecommending(true);
    try {
      const response = await adminApi.assignMultipleProducts(company.id, selectedProductIds);
      const count = response.created_orders?.length || 0;
      const skipped = response.skipped?.length || 0;
      
      if (count > 0) {
        toast.success(`${count} product(s) assigned successfully! Draft invoices created.`);
      }
      if (skipped > 0) {
        toast.warning(`${skipped} product(s) skipped (already assigned)`);
      }
      
      setShowProductModal(false);
      setSelectedProductIds([]);
      setProductSearch('');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to assign products'));
    } finally {
      setRecommending(false);
    }
  };

  // Filter products by search AND by country (match company's country)
  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.category?.toLowerCase().includes(productSearch.toLowerCase());
    
    // Get company's country
    const companyCountry = isUSCompany(company) ? 'US' : 'IN';
    // Get product's country (default to IN if not set)
    const productCountry = p.country || 'IN';
    
    // Only show products that match the company's country
    const matchesCountry = productCountry === companyCountry;
    
    return matchesSearch && matchesCountry;
  });

  // Reset form when company changes
  useEffect(() => {
    if (company) {
      setIsEditing(false);
      setGstTdsForm({
        gst_scheme: company?.gst_scheme || '',
        gst_rates_applicable: company?.gst_rates_applicable || [],
        lut_filed: company?.lut_filed || false,
        approx_annual_turnover: company?.approx_annual_turnover || '',
        additional_gstins: company?.additional_gstins || [],
        gst_filing_type: company?.gst_filing_type || '',
        gst_data_type: company?.gst_data_type || '',
        gst_data_types: company?.gst_data_types || [],
        marketplace: company?.marketplace || '',
        marketplaces: company?.marketplaces || [],
        gst_fy: company?.gst_fy || '',
        gst_start_month: company?.gst_start_month || '',
        gst_start_quarter: company?.gst_start_quarter || '',
        hsn_sac_codes: company?.hsn_sac_codes || [],
        gst_subscription_amount: company?.gst_subscription_amount || '',
        gst_discount_percent: company?.gst_discount_percent || 0,
        einvoice_applicable: company?.einvoice_applicable || false,
        billing_model: company?.billing_model || 'per_filing',
        subscription_status: company?.subscription_status || '',
        subscription_start_date: company?.subscription_start_date || '',
        subscription_end_date: company?.subscription_end_date || '',
        remaining_filings: company?.remaining_filings ?? '',
      });
    }
  }, [company?.id]);

  const handleSaveGstTds = async () => {
    setSavingGstTds(true);
    try {
      const payload = {
        gst_scheme: gstTdsForm.gst_scheme || null,
        gst_rates_applicable: gstTdsForm.gst_rates_applicable?.length ? gstTdsForm.gst_rates_applicable : [],
        lut_filed: gstTdsForm.lut_filed,
        approx_annual_turnover: gstTdsForm.approx_annual_turnover ? Number(gstTdsForm.approx_annual_turnover) : null,
        additional_gstins: gstTdsForm.additional_gstins?.length ? gstTdsForm.additional_gstins : [],
        gst_filing_type: gstTdsForm.gst_filing_type || null,
        gst_data_type: gstTdsForm.gst_data_type || null,
        gst_data_types: gstTdsForm.gst_data_types?.length ? gstTdsForm.gst_data_types : [],
        marketplace: gstTdsForm.gst_data_type === 'marketplace' ? (gstTdsForm.marketplace || null) : null,
        marketplaces: gstTdsForm.gst_data_type === 'marketplace' && gstTdsForm.marketplace ? [gstTdsForm.marketplace] : [],
        gst_fy: gstTdsForm.gst_fy || null,
        gst_start_month: gstTdsForm.gst_start_month || null,
        gst_start_quarter: gstTdsForm.gst_start_quarter || null,
        hsn_sac_codes: gstTdsForm.hsn_sac_codes?.length ? gstTdsForm.hsn_sac_codes : [],
        gst_subscription_amount: gstTdsForm.gst_subscription_amount ? Number(gstTdsForm.gst_subscription_amount) : null,
        gst_discount_percent: gstTdsForm.gst_discount_percent ? Number(gstTdsForm.gst_discount_percent) : 0,
        einvoice_applicable: gstTdsForm.einvoice_applicable,
        billing_model: gstTdsForm.billing_model || 'per_filing',
        subscription_status: gstTdsForm.subscription_status || null,
        subscription_start_date: gstTdsForm.subscription_start_date || null,
        subscription_end_date: gstTdsForm.subscription_end_date || null,
        remaining_filings: gstTdsForm.remaining_filings !== '' ? Number(gstTdsForm.remaining_filings) : null,
      };
      await adminApi.updateCompany(company.id, payload);
      toast.success('GST & TDS profile saved');

      // Auto-generate GST orders if scheme + FY + start period + GSTIN are set
      const hasGstin = company?.gstin || (gstTdsForm.additional_gstins || []).some(ag => ag.gstin?.trim());
      const hasStartPeriod = (gstTdsForm.gst_scheme === 'regular' && gstTdsForm.gst_start_month) ||
                              (gstTdsForm.gst_scheme === 'qrmp' && gstTdsForm.gst_start_quarter);
      if (gstTdsForm.gst_scheme && gstTdsForm.gst_fy && hasStartPeriod && hasGstin) {
        try {
          const genRes = await api.post(`/admin/companies/${company.id}/gst/generate-orders`);
          const data = genRes.data;
          if (data.orders_created > 0) {
            toast.success(`${data.orders_created} GST filing order(s) generated`);
          }
          if (data.stale_removed > 0) {
            toast.info(`${data.stale_removed} stale order(s) from old scheme removed`);
          }
          if (data.orders_created === 0 && !data.stale_removed) {
            toast.info('All orders already exist — nothing new to generate');
          }
        } catch (genErr) {
          const msg = genErr?.response?.data?.detail || 'Failed to auto-generate orders';
          toast.error(msg);
        }
      }

      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to save GST & TDS profile');
    } finally {
      setSavingGstTds(false);
    }
  };

  const handleSwitchToPerFiling = async () => {
    if (!switchReason.trim()) return;
    setSwitchingBilling(true);
    try {
      const res = await api.put(`/admin/companies/${company.id}/switch-to-per-filing`, { reason: switchReason });
      const data = res.data;
      toast.success(`Switched to per-filing. ${data.orders_updated || 0} order(s) updated.`);
      setShowSwitchModal(false);
      setSwitchReason('');
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to switch billing model');
    } finally {
      setSwitchingBilling(false);
    }
  };


  const handleEdit = () => {
    const isUS = isUSCompany(company);
    
    // Base fields for both India and US
    const baseForm = {
      company_name: company?.company_name || '',
      entity_type: company?.entity_type || 'private_limited',
      state: company?.state || '',
      description: company?.description || '',
      business_activity: company?.business_activity || '',
      registered_address: company?.registered_address || '',
      company_phone: company?.company_phone || '',
      company_email: company?.company_email || '',
      country: company?.country || 'IN',
    };
    
    if (isUS) {
      // US-specific fields
      setEditForm({
        ...baseForm,
        // Registration Details
        ein: company?.ein || '',
        us_state_of_formation: company?.us_state_of_formation || company?.state || '',
        us_formation_date: company?.us_formation_date || company?.date_of_incorporation || '',
        // BOIR
        boir_filed: company?.boir_filed || false,
        // Business Address
        us_business_address: company?.us_business_address || company?.registered_address || '',
        us_business_city: company?.us_business_city || '',
        us_business_state: company?.us_business_state || '',
        us_business_zip: company?.us_business_zip || '',
        // Registered Agent
        use_our_registered_agent: company?.use_our_registered_agent ?? true,
        registered_agent_name: company?.registered_agent_name || '',
        registered_agent_address: company?.registered_agent_address || '',
        registered_agent_expiry_date: company?.registered_agent_expiry_date || '',
        registered_agent_status: company?.registered_agent_status || 'active',
        // Mail Forwarding
        use_our_mail_forwarding: company?.use_our_mail_forwarding ?? false,
        mail_forwarding_name: company?.mail_forwarding_name || '',
        mail_forwarding_address: company?.mail_forwarding_address || '',
        mail_forwarding_city: company?.mail_forwarding_city || '',
        mail_forwarding_state: company?.mail_forwarding_state || '',
        mail_forwarding_zip: company?.mail_forwarding_zip || '',
        mail_forwarding_expiry_date: company?.mail_forwarding_expiry_date || '',
        mail_forwarding_status: company?.mail_forwarding_status || 'active',
        // Tax Details
        us_tax_classification: company?.us_tax_classification || '',
        state_tax_id: company?.state_tax_id || '',
        // Website
        website: company?.website || '',
      });
    } else {
      // India-specific fields
      setEditForm({
        ...baseForm,
        cin_llpin: company?.cin_llpin || '',
        date_of_incorporation: company?.date_of_incorporation || '',
        authorized_capital: company?.capital_structure?.authorized_capital || 0,
        paid_up_capital: company?.capital_structure?.paid_up_capital || 0,
        share_face_value: company?.capital_structure?.share_face_value || 10,
        contribution: company?.capital_structure?.contribution || '',
        // Location & Jurisdiction
        police_station_jurisdiction: company?.police_station_jurisdiction || '',
        google_location_lat: company?.google_location_lat || '',
        google_location_lng: company?.google_location_lng || '',
        // GST Details
        gstin: company?.gstin || '',
        gst_state_code: company?.gst_state_code || '',
        // V3 Credentials
        v3_username: company?.v3_username || '',
        v3_password: company?.v3_password || '',
        v3_hint_question: company?.v3_hint_question || '',
      });
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const isUS = isUSCompany(company);
    
    try {
      let updatePayload = {
        company_name: editForm.company_name,
        entity_type: editForm.entity_type,
        state: editForm.state,
        description: editForm.description,
        business_activity: editForm.business_activity,
        registered_address: editForm.registered_address,
        company_phone: editForm.company_phone,
        company_email: editForm.company_email,
        country: editForm.country,
      };
      
      if (isUS) {
        // US-specific fields
        updatePayload = {
          ...updatePayload,
          ein: editForm.ein || null,
          us_state_of_formation: editForm.us_state_of_formation || null,
          us_formation_date: editForm.us_formation_date || null,
          date_of_incorporation: editForm.us_formation_date || null, // Sync with formation date
          boir_filed: editForm.boir_filed || false,
          // Business Address
          us_business_address: editForm.us_business_address || null,
          us_business_city: editForm.us_business_city || null,
          us_business_state: editForm.us_business_state || null,
          us_business_zip: editForm.us_business_zip || null,
          // Registered Agent
          use_our_registered_agent: editForm.use_our_registered_agent,
          registered_agent_name: editForm.registered_agent_name || null,
          registered_agent_address: editForm.registered_agent_address || null,
          registered_agent_expiry_date: editForm.registered_agent_expiry_date || null,
          registered_agent_status: editForm.registered_agent_status || 'active',
          // Mail Forwarding
          use_our_mail_forwarding: editForm.use_our_mail_forwarding,
          mail_forwarding_name: editForm.mail_forwarding_name || null,
          mail_forwarding_address: editForm.mail_forwarding_address || null,
          mail_forwarding_city: editForm.mail_forwarding_city || null,
          mail_forwarding_state: editForm.mail_forwarding_state || null,
          mail_forwarding_zip: editForm.mail_forwarding_zip || null,
          mail_forwarding_expiry_date: editForm.mail_forwarding_expiry_date || null,
          mail_forwarding_status: editForm.mail_forwarding_status || 'active',
          // Tax Details
          us_tax_classification: editForm.us_tax_classification || null,
          state_tax_id: editForm.state_tax_id || null,
          website: editForm.website || null,
        };
      } else {
        // India-specific fields
        const _editCfg = entityConfig(editForm.entity_type || company?.entity_type);
        updatePayload = {
          ...updatePayload,
          cin_llpin: editForm.cin_llpin || null,
          date_of_incorporation: editForm.date_of_incorporation || null,
          capital_structure: _editCfg.capital_kind === 'contribution'
            ? { contribution: parseFloat(editForm.contribution) || 0 }
            : _editCfg.capital_kind === 'none'
              ? null
              : {
                  authorized_capital: parseInt(editForm.authorized_capital) || 0,
                  paid_up_capital: parseInt(editForm.paid_up_capital) || 0,
                  share_face_value: parseInt(editForm.share_face_value) || 10,
                },
          // Location & Jurisdiction
          police_station_jurisdiction: editForm.police_station_jurisdiction || null,
          google_location_lat: editForm.google_location_lat || null,
          google_location_lng: editForm.google_location_lng || null,
          // GST Details
          gstin: editForm.gstin || null,
          gst_state_code: editForm.gst_state_code || null,
          // V3 Credentials
          v3_username: editForm.v3_username || null,
          v3_password: editForm.v3_password || null,
          v3_hint_question: editForm.v3_hint_question || null,
        };
      }
      
      await adminApi.updateCompany(company.id, updatePayload);
      toast.success('Company updated successfully');
      setIsEditing(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  if (!company) {
    return (
      <div className="admin-card p-8 flex flex-col items-center justify-center text-center h-96">
        <Building2 className="w-16 h-16 admin-text-soft mb-4" />
        <p className="admin-text-dim text-lg">Select a company to view details</p>
        <p className="admin-text-soft text-sm mt-1">Click on any company card to see their information</p>
      </div>
    );
  }

  const isUS = isUSCompany(company);

  return (
    <>
    <div className="admin-card overflow-hidden">
      {/* Company Header */}
      <div className={`p-6 border-b border-[var(--admin-border)] ${
        isUS ? 'bg-gradient-to-r from-red-600/20 to-blue-600/20' : 'bg-gradient-to-r from-blue-600/20 to-cyan-600/20'
      }`}>
        {onBack && (
          <Button variant="ghost" size="sm" onClick={onBack} className="mb-4 admin-text-dim hover:text-[var(--admin-text)] -ml-2">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to User
          </Button>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-[var(--admin-text)] font-bold text-2xl ${
              isUS ? 'bg-gradient-to-br from-red-500 to-blue-600' : 'bg-gradient-to-br from-blue-500 to-cyan-500'
            }`}>
              {company.company_name?.charAt(0)?.toUpperCase() || 'C'}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-xl font-bold text-[var(--admin-text)]">{company.company_name}</h2>
              </div>
              <p className="admin-text-dim capitalize">
                {company.entity_type?.replace('_', ' ')}
              </p>
              {company.cin_llpin && <p className="admin-text-soft text-sm font-mono mt-1">CIN/LLPIN: {company.cin_llpin}</p>}
              {company.gstin && <p className="admin-text-soft text-sm font-mono mt-0.5">GSTIN: {company.gstin}</p>}
            </div>
          </div>
          {!isEditing ? (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setShowProductModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" /> Assign Product
              </Button>
              <Button variant="outline" size="sm" onClick={handleEdit} className="border-[var(--admin-border)] admin-text-dim hover:bg-[var(--admin-bg-hover)]">
                <Pencil className="w-4 h-4 mr-2" /> Edit
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} className="admin-text-dim">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Company Details */}
      <div className="p-6 space-y-6 max-h-[calc(100vh-400px)] overflow-y-auto">

        {/* Active Service Flags — Read-only, auto-computed from expiry dates */}
        {isUS && !isEditing && (
          <div className="flex items-center gap-3 flex-wrap" data-testid="active-service-flags">
            {/* Registered Agent Flag — computed from company's registered_agent_expiry_date */}
            {(() => {
              const raStatus = computeServiceStatus(company.registered_agent_expiry_date);
              const isActive = raStatus.status === 'active' || raStatus.status === 'due_soon' || raStatus.status === 'expiring_soon';
              return (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
                  isActive
                    ? raStatus.status === 'expiring_soon'
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                      : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'admin-bg-card-inner border-[var(--admin-border)] admin-text-soft'
                }`} data-testid="ra-service-flag">
                  <CheckCircle2 className={`w-4 h-4 ${isActive ? '' : 'opacity-30'}`} />
                  <span className="font-medium">Registered Agent</span>
                  {isActive ? (
                    <span className="text-xs opacity-75">({raStatus.days}d remaining)</span>
                  ) : (
                    <span className="text-xs opacity-50">{raStatus.status === 'expired' ? 'Expired' : 'No active service'}</span>
                  )}
                </div>
              );
            })()}

            {/* Mail Forwarding Flag — computed from company's mail_forwarding_expiry_date */}
            {(() => {
              const mfStatus = computeServiceStatus(company.mail_forwarding_expiry_date);
              const isActive = mfStatus.status === 'active' || mfStatus.status === 'due_soon' || mfStatus.status === 'expiring_soon';
              return (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
                  isActive
                    ? mfStatus.status === 'expiring_soon'
                      ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                      : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'admin-bg-card-inner border-[var(--admin-border)] admin-text-soft'
                }`} data-testid="mf-service-flag">
                  <CheckCircle2 className={`w-4 h-4 ${isActive ? '' : 'opacity-30'}`} />
                  <span className="font-medium">Mail Forwarding</span>
                  {isActive ? (
                    <span className="text-xs opacity-75">({mfStatus.days}d remaining)</span>
                  ) : (
                    <span className="text-xs opacity-50">{mfStatus.status === 'expired' ? 'Expired' : 'No active service'}</span>
                  )}
                </div>
              );
            })()}
          </div>
        )}
        {isEditing ? (
          <div className="space-y-4">
            {/* Summary Panel in Edit Mode */}
            <SummaryPanel 
              directorsCount={directors?.length || 0}
              shareholdersCount={shareholders?.length || 0}
              documentsCount={0}
            />

            <h3 className="text-sm font-semibold admin-text-dim uppercase tracking-wider">Business Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs admin-text-soft mb-1 block">Company Name *</label>
                <Input value={editForm.company_name} onChange={(e) => setEditForm({...editForm, company_name: e.target.value})}
                  className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" />
              </div>
              <div>
                <label className="text-xs admin-text-soft mb-1 block">Entity Type *</label>
                <select value={editForm.entity_type} onChange={(e) => setEditForm({...editForm, entity_type: e.target.value})}
                  className="w-full rounded-md admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)] p-2 text-sm">
                  {(isUS ? US_ENTITY_TYPES : INDIA_ENTITY_TYPES).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              
              {/* US-specific fields */}
              {isUS ? (
                <>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">Formation State *</label>
                    <select value={editForm.us_state_of_formation || editForm.state} onChange={(e) => setEditForm({...editForm, us_state_of_formation: e.target.value, state: e.target.value})}
                      className="w-full rounded-md admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)] p-2 text-sm">
                      <option value="">Select State</option>
                      {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">Nature of Business</label>
                    <Input value={editForm.business_activity} onChange={(e) => setEditForm({...editForm, business_activity: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="e.g., Software Development" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs admin-text-soft mb-1 block">Description</label>
                    <Input value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">State *</label>
                    <select value={editForm.state} onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                      className="w-full rounded-md admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)] p-2 text-sm">
                      <option value="">Select State</option>
                      {INDIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">CIN/LLPIN</label>
                    <Input value={editForm.cin_llpin} onChange={(e) => setEditForm({...editForm, cin_llpin: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="U74999MH2020PTC123456" />
                  </div>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">Date of Incorporation</label>
                    <Input type="date" value={editForm.date_of_incorporation} onChange={(e) => setEditForm({...editForm, date_of_incorporation: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" />
                  </div>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">Business Activity</label>
                    <Input value={editForm.business_activity} onChange={(e) => setEditForm({...editForm, business_activity: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="Software Development" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs admin-text-soft mb-1 block">Description</label>
                    <Input value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" />
                  </div>
                </>
              )}
            </div>

            {/* Contact Information - Common */}
            <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider pt-4">Contact Information</h3>
            <div className="grid grid-cols-2 gap-4 bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
              <div>
                <label className="text-xs admin-text-soft mb-1 block">Business Phone</label>
                <Input value={editForm.company_phone} onChange={(e) => setEditForm({...editForm, company_phone: e.target.value})}
                  className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder={isUS ? "+1 555-123-4567" : "+91 9876543210"} />
              </div>
              <div>
                <label className="text-xs admin-text-soft mb-1 block">Business Email</label>
                <Input type="email" value={editForm.company_email} onChange={(e) => setEditForm({...editForm, company_email: e.target.value})}
                  className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="contact@company.com" />
              </div>
              {isUS && (
                <div className="col-span-2">
                  <label className="text-xs admin-text-soft mb-1 block">Website</label>
                  <Input value={editForm.website || ''} onChange={(e) => setEditForm({...editForm, website: e.target.value})}
                    className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="www.company.com" />
                </div>
              )}
            </div>

            {/* US-specific: Business Address & Registration Details */}
            {isUS ? (
              <>
                <h3 className="text-sm font-semibold text-purple-400 uppercase tracking-wider pt-4">Business Address</h3>
                <div className="grid grid-cols-2 gap-4 bg-purple-500/5 border border-purple-500/20 rounded-lg p-4">
                  <div className="col-span-2">
                    <label className="text-xs admin-text-soft mb-1 block">Street Address</label>
                    <Input value={editForm.us_business_address || ''} onChange={(e) => setEditForm({...editForm, us_business_address: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="123 Main Street, Suite 100" />
                  </div>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">City</label>
                    <Input value={editForm.us_business_city || ''} onChange={(e) => setEditForm({...editForm, us_business_city: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="San Francisco" />
                  </div>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">State</label>
                    <select value={editForm.us_business_state || ''} onChange={(e) => setEditForm({...editForm, us_business_state: e.target.value})}
                      className="w-full rounded-md admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)] p-2 text-sm">
                      <option value="">Select State</option>
                      {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">ZIP Code</label>
                    <Input value={editForm.us_business_zip || ''} onChange={(e) => setEditForm({...editForm, us_business_zip: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="94105" maxLength={10} />
                  </div>
                </div>

                <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider pt-4">Registration Details</h3>
                <div className="grid grid-cols-2 gap-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">Legal Name</label>
                    <Input value={editForm.company_name} disabled
                      className="admin-bg-inner border-[var(--admin-border)] admin-text-dim" />
                  </div>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">EIN (Federal Tax ID)</label>
                    <Input value={editForm.ein || ''} onChange={(e) => setEditForm({...editForm, ein: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)] font-mono" placeholder="XX-XXXXXXX" />
                  </div>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">State Tax ID</label>
                    <Input value={editForm.state_tax_id || ''} onChange={(e) => setEditForm({...editForm, state_tax_id: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="State specific tax ID" />
                  </div>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">Registration Date</label>
                    <Input type="date" value={editForm.us_formation_date || ''} onChange={(e) => setEditForm({...editForm, us_formation_date: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" />
                  </div>
                  <div className="col-span-2 flex items-center gap-3 admin-field p-4">
                    <input type="checkbox" id="boir_filed" checked={editForm.boir_filed || false} 
                      onChange={(e) => setEditForm({...editForm, boir_filed: e.target.checked})}
                      className="w-5 h-5 rounded admin-bg-card-inner border-[var(--admin-border)]" />
                    <label htmlFor="boir_filed" className="text-sm admin-text-dim">BOIR (Beneficial Ownership Information Report) Filed</label>
                  </div>
                </div>

                <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider pt-4">Registered Agent (Annual Renewal)</h3>
                <div className="grid grid-cols-2 gap-4 bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
                  <div className="col-span-2 flex items-center gap-3 admin-field p-4 mb-2">
                    <input type="checkbox" id="use_our_ra" checked={editForm.use_our_registered_agent} 
                      onChange={(e) => setEditForm({...editForm, use_our_registered_agent: e.target.checked})}
                      className="w-5 h-5 rounded admin-bg-card-inner border-[var(--admin-border)]" />
                    <label htmlFor="use_our_ra" className="text-sm admin-text-dim">Using our Registered Agent service</label>
                  </div>
                  {!editForm.use_our_registered_agent && (
                    <>
                      <div className="col-span-2">
                        <label className="text-xs admin-text-soft mb-1 block">Registered Agent Name</label>
                        <Input value={editForm.registered_agent_name || ''} onChange={(e) => setEditForm({...editForm, registered_agent_name: e.target.value})}
                          className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="Agent Name or Company" />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs admin-text-soft mb-1 block">Registered Agent Address</label>
                        <Input value={editForm.registered_agent_address || ''} onChange={(e) => setEditForm({...editForm, registered_agent_address: e.target.value})}
                          className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="Full address" />
                      </div>
                    </>
                  )}
                  {/* RA Service Expiry & Status - Common for both */}
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">RA Service Expiry Date</label>
                    <Input type="date" value={editForm.registered_agent_expiry_date || ''} 
                      onChange={(e) => setEditForm({...editForm, registered_agent_expiry_date: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" />
                  </div>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">RA Status (auto-computed)</label>
                    {(() => {
                      const s = computeServiceStatus(editForm.registered_agent_expiry_date);
                      return <span className={`inline-block text-xs px-2 py-1.5 rounded-full ${s.color}`}>{s.label}{s.days !== undefined ? ` (${s.days < 0 ? Math.abs(s.days) + 'd overdue' : s.days + 'd left'})` : ''}</span>;
                    })()}
                  </div>
                  {/* Warning about RA importance */}
                  <div className="col-span-2 bg-red-500/10 border border-red-500/30 rounded-lg p-3 mt-2">
                    <p className="text-xs text-red-400">
                      ⚠️ <strong>Important:</strong> Every US company must maintain an active Registered Agent. 
                      If RA service expires or resigns, the company may lose good standing and face dissolution.
                    </p>
                  </div>
                </div>

                <h3 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider pt-4">Mail Forwarding Agent (Annual Renewal)</h3>
                <div className="grid grid-cols-2 gap-4 bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-4">
                  <div className="col-span-2 flex items-center gap-3 admin-field p-4 mb-2">
                    <input type="checkbox" id="use_our_mf" checked={editForm.use_our_mail_forwarding} 
                      onChange={(e) => setEditForm({...editForm, use_our_mail_forwarding: e.target.checked})}
                      className="w-5 h-5 rounded admin-bg-card-inner border-[var(--admin-border)]" />
                    <label htmlFor="use_our_mf" className="text-sm admin-text-dim">Using our Mail Forwarding service</label>
                  </div>
                  {!editForm.use_our_mail_forwarding && (
                    <>
                      <div className="col-span-2">
                        <label className="text-xs admin-text-soft mb-1 block">Mail Forwarding Agent Name</label>
                        <Input value={editForm.mail_forwarding_name || ''} onChange={(e) => setEditForm({...editForm, mail_forwarding_name: e.target.value})}
                          className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="Agent Name or Company" />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs admin-text-soft mb-1 block">Mail Forwarding Address</label>
                        <Input value={editForm.mail_forwarding_address || ''} onChange={(e) => setEditForm({...editForm, mail_forwarding_address: e.target.value})}
                          className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="Full address" />
                      </div>
                      <div>
                        <label className="text-xs admin-text-soft mb-1 block">City</label>
                        <Input value={editForm.mail_forwarding_city || ''} onChange={(e) => setEditForm({...editForm, mail_forwarding_city: e.target.value})}
                          className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" />
                      </div>
                      <div>
                        <label className="text-xs admin-text-soft mb-1 block">State</label>
                        <select value={editForm.mail_forwarding_state || ''} onChange={(e) => setEditForm({...editForm, mail_forwarding_state: e.target.value})}
                          className="w-full rounded-md admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)] p-2 text-sm">
                          <option value="">Select State</option>
                          {US_STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs admin-text-soft mb-1 block">ZIP Code</label>
                        <Input value={editForm.mail_forwarding_zip || ''} onChange={(e) => setEditForm({...editForm, mail_forwarding_zip: e.target.value})}
                          className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="94105" maxLength={10} />
                      </div>
                    </>
                  )}
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">MF Service Expiry Date</label>
                    <Input type="date" value={editForm.mail_forwarding_expiry_date || ''} 
                      onChange={(e) => setEditForm({...editForm, mail_forwarding_expiry_date: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" />
                  </div>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">MF Status (auto-computed)</label>
                    {(() => {
                      const s = computeServiceStatus(editForm.mail_forwarding_expiry_date);
                      return <span className={`inline-block text-xs px-2 py-1.5 rounded-full ${s.color}`}>{s.label}{s.days !== undefined ? ` (${s.days < 0 ? Math.abs(s.days) + 'd overdue' : s.days + 'd left'})` : ''}</span>;
                    })()}
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* India-specific sections */}
                <div className="col-span-2">
                  <label className="text-xs admin-text-soft mb-1 block">Registered Address</label>
                  <Input value={editForm.registered_address} onChange={(e) => setEditForm({...editForm, registered_address: e.target.value})}
                    className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" />
                </div>

                <h3 className="text-sm font-semibold text-purple-400 uppercase tracking-wider pt-4">Location & Jurisdiction</h3>
                <div className="grid grid-cols-2 gap-4 bg-purple-500/5 border border-purple-500/20 rounded-lg p-4">
                  <div className="col-span-2">
                    <label className="text-xs admin-text-soft mb-1 block">Police Station Jurisdiction</label>
                    <Input value={editForm.police_station_jurisdiction} onChange={(e) => setEditForm({...editForm, police_station_jurisdiction: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="e.g., City Center Police Station" />
                  </div>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">Google Location - Latitude</label>
                    <Input value={editForm.google_location_lat} onChange={(e) => setEditForm({...editForm, google_location_lat: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="e.g., 26.2183" />
                  </div>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">Google Location - Longitude</label>
                    <Input value={editForm.google_location_lng} onChange={(e) => setEditForm({...editForm, google_location_lng: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="e.g., 78.1828" />
                  </div>
                </div>

                <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider pt-4">GST Details</h3>
                <div className="grid grid-cols-2 gap-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">GSTIN (GST Number)</label>
                    <Input value={editForm.gstin} onChange={(e) => {
                      const val = e.target.value.toUpperCase();
                      const sc = val.length >= 2 ? val.substring(0, 2) : '';
                      setEditForm({...editForm, gstin: val, gst_state_code: sc});
                    }}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)] font-mono" placeholder="e.g., 19AAJCG1273H1ZH" maxLength={15} />
                  </div>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">GST State Code (auto-calculated)</label>
                    <Input value={editForm.gst_state_code} readOnly
                      className="admin-bg-inner/60 border-[var(--admin-border)] admin-text-dim font-mono cursor-not-allowed" />
                  </div>
                </div>

                {(() => {
                  const editCfg = entityConfig(editForm.entity_type || company?.entity_type);
                  if (editCfg.capital_kind === 'none') return null;
                  if (editCfg.capital_kind === 'contribution') {
                    return (
                      <>
                        <h3 className="text-sm font-semibold admin-text-dim uppercase tracking-wider pt-4">Capital / Contribution</h3>
                        <div>
                          <label className="text-xs admin-text-soft mb-1 block">Total Contribution (₹)</label>
                          <Input type="number" value={editForm.contribution || ''}
                            onChange={(e) => setEditForm({ ...editForm, contribution: e.target.value })}
                            className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]"
                            data-testid="edit-contribution" />
                          <p className="text-[11px] admin-text-soft mt-1">
                            {editCfg.label} entities don&apos;t issue shares — capture the pooled contribution here.
                          </p>
                        </div>
                      </>
                    );
                  }
                  return (
                    <>
                      <h3 className="text-sm font-semibold admin-text-dim uppercase tracking-wider pt-4">Capital Structure</h3>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="text-xs admin-text-soft mb-1 block">Authorized Capital (₹)</label>
                          <Input type="number" value={editForm.authorized_capital} onChange={(e) => setEditForm({ ...editForm, authorized_capital: e.target.value })}
                            className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" />
                        </div>
                        <div>
                          <label className="text-xs admin-text-soft mb-1 block">Paid-up Capital (₹)</label>
                          <Input type="number" value={editForm.paid_up_capital} onChange={(e) => setEditForm({ ...editForm, paid_up_capital: e.target.value })}
                            className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" />
                        </div>
                        <div>
                          <label className="text-xs admin-text-soft mb-1 block">Share Face Value (₹)</label>
                          <Input type="number" value={editForm.share_face_value} onChange={(e) => setEditForm({ ...editForm, share_face_value: e.target.value })}
                            className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" />
                        </div>
                      </div>
                    </>
                  );
                })()}

                <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider pt-4">V3 Credentials</h3>
                <div className="grid grid-cols-2 gap-4 bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">Username</label>
                    <Input value={editForm.v3_username} onChange={(e) => setEditForm({...editForm, v3_username: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="V3 Username" />
                  </div>
                  <div>
                    <label className="text-xs admin-text-soft mb-1 block">Password</label>
                    <Input type="text" value={editForm.v3_password} onChange={(e) => setEditForm({...editForm, v3_password: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="V3 Password" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs admin-text-soft mb-1 block">Security Hint Question</label>
                    <Input value={editForm.v3_hint_question} onChange={(e) => setEditForm({...editForm, v3_hint_question: e.target.value})}
                      className="admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]" placeholder="Security hint question" />
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            {/* DISPLAY VIEW - Country-aware */}
            
            {/* Business Information Section */}
            <div>
              <h3 className="text-sm font-semibold admin-text-dim uppercase tracking-wider mb-3">Business Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <DisplayField label="Company Name" value={company.company_name} hideIfEmpty={false} />
                <div className="admin-field p-3">
                  <p className="text-xs admin-text-soft mb-1">Entity Type</p>
                  <StatusBadge status={company.entity_type} type="entity_type" />
                </div>
                
                {isUS ? (
                  <>
                    <DisplayField 
                      label="Formation State" 
                      value={getUSStateName(company.us_state_of_formation || company.state)} 
                    />
                    <DisplayField label="EIN (Federal Tax ID)" value={company.ein} mono={true} />
                    <DisplayField label="Registration Date" value={company.us_formation_date || company.date_of_incorporation} />
                    <DisplayField label="Nature of Business" value={company.business_activity} />
                  </>
                ) : (
                  <>
                    <DisplayField label="State" value={company.state} />
                    <DisplayField label="CIN/LLPIN" value={company.cin_llpin} mono={true} />
                    <DisplayField label="Date of Incorporation" value={company.date_of_incorporation} />
                    <DisplayField label="Business Activity" value={company.business_activity} />
                  </>
                )}
                
                <DisplayField label="Business Phone" value={company.company_phone} />
                <DisplayField label="Business Email" value={company.company_email} />
                {isUS && <DisplayField label="Website" value={company.website} />}
                {company.description && (
                  <div className="col-span-2 admin-field p-3">
                    <p className="text-xs admin-text-soft mb-1">Description</p>
                    <p className="text-[var(--admin-text)] text-sm">{company.description}</p>
                  </div>
                )}
              </div>
            </div>

            {/* US-specific: Business Address & Registration Details */}
            {isUS && (
              <>
                {/* Business Address */}
                {(company.us_business_address || company.us_business_city || company.registered_address) && (
                  <div>
                    <h3 className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-3">Business Address</h3>
                    <div className="grid grid-cols-2 gap-4 bg-purple-500/5 border border-purple-500/20 rounded-lg p-4">
                      {company.us_business_address ? (
                        <>
                          <DisplayField label="Street Address" value={company.us_business_address} />
                          <DisplayField label="City" value={company.us_business_city} />
                          <DisplayField label="State" value={getUSStateName(company.us_business_state)} />
                          <DisplayField label="ZIP Code" value={company.us_business_zip} mono={true} />
                        </>
                      ) : (
                        <div className="col-span-2 admin-field p-3">
                          <p className="text-xs admin-text-soft mb-1">Address</p>
                          <p className="text-[var(--admin-text)] text-sm">{company.registered_address}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Registration Details */}
                <div>
                  <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider mb-3">Registration Details</h3>
                  <div className="grid grid-cols-2 gap-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
                    <DisplayField label="State Tax ID" value={company.state_tax_id} mono={true} />
                    <div className="admin-field p-3">
                      <p className="text-xs admin-text-soft mb-1">BOIR Filed</p>
                      <span className={`text-sm px-2 py-0.5 rounded-full ${
                        company.boir_filed ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {company.boir_filed ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Registered Agent */}
                <div>
                  <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider mb-3">Registered Agent (Annual Renewal Required)</h3>
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4 space-y-4">
                    {/* RA Details */}
                    {company.use_our_registered_agent ? (
                      <div className="flex items-center gap-3 text-amber-400">
                        <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                          <CheckCircle2 className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">Using StartEase Registered Agent Service</p>
                          <p className="text-xs admin-text-dim mt-1">Our team handles all registered agent requirements</p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        <DisplayField label="Agent Name" value={company.registered_agent_name} />
                        <div className="col-span-2">
                          <DisplayField 
                            label="Agent Address" 
                            value={[
                              company.registered_agent_address,
                              company.registered_agent_city,
                              company.registered_agent_state,
                              company.registered_agent_zip
                            ].filter(Boolean).join(', ')} 
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Expiry Info — Status auto-computed from date */}
                    {(() => {
                      const raStatus = computeServiceStatus(company.registered_agent_expiry_date);
                      return (
                        <div className="grid grid-cols-2 gap-4 pt-3 border-t border-amber-500/20">
                          <div className="admin-field p-3">
                            <p className="text-xs admin-text-soft mb-1">RA Service Expiry Date</p>
                            <p className={`text-sm font-medium ${raStatus.status === 'expired' || raStatus.status === 'expiring_soon' ? 'text-red-400' : 'text-[var(--admin-text)]'}`}>
                              {company.registered_agent_expiry_date 
                                ? new Date(company.registered_agent_expiry_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                                : 'Not Set'}
                            </p>
                          </div>
                          <div className="admin-field p-3">
                            <p className="text-xs admin-text-soft mb-1">RA Status</p>
                            <span className={`text-xs px-2 py-1 rounded-full ${raStatus.color}`}>
                              {raStatus.label}
                            </span>
                            {raStatus.days !== undefined && raStatus.days !== null && (
                              <span className="text-xs admin-text-soft ml-2">
                                ({raStatus.days < 0 ? `${Math.abs(raStatus.days)}d overdue` : `${raStatus.days}d left`})
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                      
                    {/* Warning Alert */}
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mt-3">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-red-400 text-lg">⚠️</span>
                        </div>
                        <div>
                          <p className="text-red-400 font-medium text-sm">Important: Registered Agent Requirements</p>
                          <ul className="text-xs admin-text-dim mt-2 space-y-1 list-disc list-inside">
                            <li>Every US company must maintain an active Registered Agent at all times</li>
                            <li>If your RA resigns or service expires, the state may administratively dissolve your company</li>
                            <li>You may lose good standing status and face penalties or fines</li>
                            <li>Legal documents and lawsuits cannot be properly served without an active RA</li>
                          </ul>
                          <p className="text-xs text-amber-400 mt-2 font-medium">
                            💡 Renew your RA service before expiry to maintain compliance
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mail Forwarding Agent */}
                <div>
                  <h3 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider mb-3">Mail Forwarding Agent (Annual Renewal Required)</h3>
                  <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-4 space-y-4">
                    {company.use_our_mail_forwarding ? (
                      <div className="flex items-center gap-3 text-cyan-400">
                        <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                          <CheckCircle2 className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">Using Our Mail Forwarding Service</p>
                          <p className="text-xs admin-text-dim mt-1">Our team handles all mail forwarding requirements</p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        <DisplayField label="Agent Name" value={company.mail_forwarding_name} />
                        <div className="col-span-2">
                          <DisplayField 
                            label="Agent Address" 
                            value={[
                              company.mail_forwarding_address,
                              company.mail_forwarding_city,
                              company.mail_forwarding_state,
                              company.mail_forwarding_zip
                            ].filter(Boolean).join(', ')} 
                          />
                        </div>
                      </div>
                    )}
                    
                    {(() => {
                      const mfStatus = computeServiceStatus(company.mail_forwarding_expiry_date);
                      return (
                        <div className="grid grid-cols-2 gap-4 pt-3 border-t border-cyan-500/20">
                          <div className="admin-field p-3">
                            <p className="text-xs admin-text-soft mb-1">MF Service Expiry Date</p>
                            <p className={`text-sm font-medium ${mfStatus.status === 'expired' || mfStatus.status === 'expiring_soon' ? 'text-red-400' : 'text-[var(--admin-text)]'}`}>
                              {company.mail_forwarding_expiry_date 
                                ? new Date(company.mail_forwarding_expiry_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                                : 'Not Set'}
                            </p>
                          </div>
                          <div className="admin-field p-3">
                            <p className="text-xs admin-text-soft mb-1">MF Status</p>
                            <span className={`text-xs px-2 py-1 rounded-full ${mfStatus.color}`}>
                              {mfStatus.label}
                            </span>
                            {mfStatus.days !== undefined && mfStatus.days !== null && (
                              <span className="text-xs admin-text-soft ml-2">
                                ({mfStatus.days < 0 ? `${Math.abs(mfStatus.days)}d overdue` : `${mfStatus.days}d left`})
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </>
            )}

            {/* India-specific: Registered Address */}
            {!isUS && company.registered_address && (
              <div>
                <h3 className="text-sm font-semibold admin-text-dim uppercase tracking-wider mb-3">Registered Address</h3>
                <div className="admin-field p-4 flex items-start gap-3">
                  <MapPin className="w-5 h-5 admin-text-soft flex-shrink-0 mt-0.5" />
                  <p className="admin-text-dim text-sm">{company.registered_address}</p>
                </div>
              </div>
            )}

            {/* India-specific: Location & Jurisdiction Section */}
            {!isUS && (company.police_station_jurisdiction || company.google_location_lat || company.google_location_lng) && (
              <div>
                <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">Location & Jurisdiction</h3>
                <div className="grid grid-cols-2 gap-4 bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                  {company.police_station_jurisdiction && (
                    <div className="col-span-2 admin-field p-3">
                      <p className="text-xs admin-text-soft mb-1">Police Station Jurisdiction</p>
                      <p className="text-[var(--admin-text)] text-sm">{company.police_station_jurisdiction}</p>
                    </div>
                  )}
                  <DisplayField label="Latitude" value={company.google_location_lat} mono={true} />
                  <DisplayField label="Longitude" value={company.google_location_lng} mono={true} />
                </div>
              </div>
            )}


            {/* India-specific: Capital Structure Section */}
            {!isUS && (
              <div>
                <h3 className="text-sm font-semibold admin-text-dim uppercase tracking-wider mb-3">
                  {cfg.capital_kind === 'contribution' ? 'Contribution' : (company.capital_label || 'Capital Structure')}
                </h3>
                {cfg.capital_kind === 'contribution' ? (
                  <div className="admin-field p-4">
                    <p className="text-xs admin-text-soft mb-1">Total Contribution</p>
                    <p className="text-xl font-bold text-emerald-400">₹{(company.capital_structure?.contribution || 0).toLocaleString()}</p>
                    <p className="text-[11px] admin-text-soft mt-1">Pooled contribution across all {cfg.directors_label.toLowerCase()}.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="admin-field p-4 text-center">
                      <p className="text-xs admin-text-soft mb-1">{company.capital_label || 'Authorized Capital'}</p>
                      <p className="text-xl font-bold text-[var(--admin-text)]">₹{(company.capital_value || company.capital_structure?.authorized_capital || 0).toLocaleString()}</p>
                    </div>
                    <div className="admin-field p-4 text-center">
                      <p className="text-xs admin-text-soft mb-1">Paid-up Capital</p>
                      <p className="text-xl font-bold text-emerald-400">₹{(company.capital_structure?.paid_up_capital || 0).toLocaleString()}</p>
                    </div>
                    <div className="admin-field p-4 text-center">
                      <p className="text-xs admin-text-soft mb-1">Share Face Value</p>
                      <p className="text-xl font-bold text-blue-400">₹{company.capital_structure?.share_face_value || 10}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* India-specific: V3 Credentials Section */}
            {!isUS && (company.v3_username || company.v3_password || company.v3_hint_question) && (
              <div>
                <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  V3 Credentials
                </h3>
                <div className="grid grid-cols-2 gap-4 bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
                  <DisplayField label="Username" value={company.v3_username} mono={true} />
                  <DisplayField label="Password" value={company.v3_password ? '••••••••' : null} />
                  {company.v3_hint_question && (
                    <div className="col-span-2 admin-field p-3">
                      <p className="text-xs admin-text-soft mb-1">Security Hint Question</p>
                      <p className="text-[var(--admin-text)] text-sm">{company.v3_hint_question}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* External Credentials Section */}
            <ExternalCredentials 
              entityType="company"
              entityId={company.id}
              entityName={company.company_name}
              isAdmin={true}
            />

            {/* Portal User / Owner (admin-only card) */}
            <div data-testid="company-owner-card">
              <h3 className="text-sm font-semibold admin-text-dim uppercase tracking-wider mb-3">
                Portal User
              </h3>
              {owner ? (
                <button
                  type="button"
                  onClick={onSelectOwner ? () => onSelectOwner(owner) : undefined}
                  disabled={!onSelectOwner}
                  className={`w-full text-left p-4 rounded-lg border border-emerald-900/30 bg-[#0f1611] transition-colors ${onSelectOwner ? 'hover:border-emerald-500/50 cursor-pointer' : 'cursor-default'}`}
                  data-testid="company-owner-details"
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Users className="w-4 h-4 text-emerald-400" />
                        <span className="font-semibold text-white" data-testid="company-owner-name">
                          {owner.full_name || '—'}
                        </span>
                        {owner.role && (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                            {owner.role.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs admin-text-dim">
                        <span className="flex items-center gap-1" data-testid="company-owner-email">
                          <Mail className="w-3.5 h-3.5" />
                          <span className="text-emerald-400">{owner.email || '—'}</span>
                        </span>
                        <span className="flex items-center gap-1" data-testid="company-owner-phone">
                          <Phone className="w-3.5 h-3.5" />
                          <span className="text-emerald-400">{owner.phone || '—'}</span>
                        </span>
                      </div>
                    </div>
                    {onSelectOwner && <ChevronRight className="w-4 h-4 admin-text-soft self-center" />}
                  </div>
                </button>
              ) : (
                <p className="admin-text-soft text-sm" data-testid="company-owner-empty">No portal user linked to this company.</p>
              )}
            </div>

            {/* Directors / Founders / Partners / Proprietor */}
            <div>
              <h3 className="text-sm font-semibold admin-text-dim uppercase tracking-wider mb-3">
                {isUS ? 'Founders & Members' : `${cfg.directors_label} & Key Personnel`} ({directors?.length || 0})
              </h3>
              {directors && directors.length > 0 ? (
                <div className="space-y-3">
                  {directors.map((director, idx) => (
                    <div key={director.id || idx} className="relative group">
                      <DirectorCard
                        director={director}
                        isFounder={idx === 0}
                        isUSCompany={isUS}
                        showProfitShare={!!cfg.has_profit_share}
                        onClick={onSelectDirector ? () => onSelectDirector(director) : undefined}
                      />
                      {cfg.show_shareholders && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openShModal({
                              shareholder_name: director.full_name,
                              shareholder_email: director.email,
                              phone: director.phone,
                              pan_number: director.pan_number,
                              aadhaar_number: director.aadhaar_number,
                              din_number: director.din_number,
                              din_status: director.din_status,
                              dsc_status: director.dsc_status,
                              dsc_expiry_date: director.dsc_expiry_date,
                              address: director.address,
                              father_name: director.father_name,
                              date_of_birth: director.date_of_birth,
                              shareholder_type: 'individual',
                              number_of_shares: 0,
                            });
                          }}
                          className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                          title={`Copy this ${cfg.director_label.toLowerCase()}'s info into a new ${cfg.shareholder_label.toLowerCase()}`}
                          data-testid={`admin-director-copy-to-shareholder-${director.id || idx}`}
                        >
                          <Plus className="w-3 h-3" /> Add as {cfg.shareholder_label}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="admin-text-soft text-sm">No {cfg.directors_label.toLowerCase()} registered</p>
              )}
            </div>

            {/* Shareholders — hidden entirely for entity types that don't have them (LLP/Partnership/Proprietorship). */}
            {cfg.show_shareholders && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold admin-text-dim uppercase tracking-wider">
                  {cfg.shareholder_label}s ({shareholders?.length || 0})
                </h3>
                <button
                  onClick={() => openShModal(null)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
                  data-testid="admin-add-shareholder-btn"
                >
                  <Plus className="w-3 h-3" /> Add {cfg.shareholder_label}
                </button>
              </div>
              {shareholders && shareholders.length > 0 ? (
                <div className="space-y-3">
                  {shareholders.map((shareholder, idx) => (
                    <ShareholderCard 
                      key={shareholder.id || idx} 
                      shareholder={shareholder}
                      onClick={onSelectShareholder ? () => onSelectShareholder(shareholder) : undefined}
                    />
                  ))}
                </div>
              ) : (
                <p className="admin-text-soft text-sm">No {cfg.shareholder_label.toLowerCase()}s registered — click <span className="text-emerald-400">Add {cfg.shareholder_label}</span> or use the &ldquo;Add as {cfg.shareholder_label}&rdquo; button on any director above to copy their info.</p>
              )}
            </div>
            )}

            {/* GST (new workflow — Phase A) — additive, does not touch legacy GST tab. */}
            <div data-testid="company-gst-section" className="admin-bg-inner border border-emerald-500/20 rounded-lg p-4">
              <GstRegistrationSection companyId={company.id} />
            </div>

            {/* Order History */}
            <div data-testid="company-order-history">
              <h3 className="text-sm font-semibold admin-text-dim uppercase tracking-wider mb-3">
                Order History ({orders?.length || 0})
              </h3>
              {orders && orders.length > 0 ? (
                <div className="space-y-3">
                  {orders.map((order, idx) => (
                    <div 
                      key={order.id || idx} 
                      className={`admin-bg-inner border border-[var(--admin-border)] rounded-lg p-4 ${onSelectOrder ? 'cursor-pointer hover:bg-[var(--admin-bg-elevated)] hover:border-[var(--admin-green)]/30 transition-colors' : ''}`}
                      onClick={onSelectOrder ? () => onSelectOrder(order) : undefined}
                      data-testid={`order-card-${order.id || idx}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-[var(--admin-text)] text-sm font-medium">{order.product?.name || order.product_id || 'Unknown Product'}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs admin-text-soft">#{order.id?.slice(-8)}</span>
                            {order.created_at && (
                              <span className="text-xs admin-text-soft">
                                {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                            )}
                            {order.amount && (
                              <span className="text-xs text-emerald-400 font-medium">
                                {order.currency === 'USD' ? '$' : '₹'}{order.amount?.toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            order.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                            order.status === 'in_progress' || order.status === 'processing' ? 'bg-blue-500/20 text-blue-400' :
                            order.status === 'pending' || order.status === 'pending_payment' ? 'bg-amber-500/20 text-amber-400' :
                            order.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                            'bg-slate-500/20 admin-text-dim'
                          }`}>
                            {order.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
                          </span>
                          {order.payment_status && (
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              order.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                              order.payment_status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-slate-500/20 admin-text-dim'
                            }`}>
                              {order.payment_status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="admin-text-soft text-sm">No orders found</p>
              )}
            </div>

            {/* Company KYC Documents */}
            <KYCDocuments 
              entityType="company"
              entityId={company.id}
              entityName={company.company_name}
              isAdmin={true}
              onRefresh={onRefresh}
              country={isUS ? 'US' : 'IN'}
            />

            {/* Last Updated Indicator */}
            <LastUpdatedBadge 
              updatedAt={company.updated_at}
              updatedByName={company.updated_by_name}
              updatedByRole={company.updated_by_role}
            />
          </>
        )}
      </div>

      {/* Assign Product Modal */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="admin-bg-card-inner border border-[var(--admin-border)] rounded-xl w-full max-w-lg max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-[var(--admin-border)] flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-[var(--admin-text)]">Assign Products</h3>
                <p className="text-sm admin-text-dim">
                  Select products for {company.company_name}
                </p>
              </div>
              <button onClick={() => { setShowProductModal(false); setSelectedProductIds([]); }} className="admin-text-dim hover:text-[var(--admin-text)]">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 border-b border-[var(--admin-border)]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 admin-text-soft" />
                <Input
                  placeholder="Search products..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="pl-9 admin-bg-inner border-[var(--admin-border)] text-[var(--admin-text)]"
                />
              </div>
              {selectedProductIds.length > 0 && (
                <div className="mt-2 text-sm text-emerald-400">
                  {selectedProductIds.length} product(s) selected
                </div>
              )}
            </div>

            <div className="p-4 max-h-[40vh] overflow-y-auto space-y-2">
              {loadingProducts ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
                </div>
              ) : filteredProducts.length > 0 ? (
                filteredProducts.map(product => {
                  const isSelected = selectedProductIds.includes(product.id);
                  return (
                    <button
                      key={product.id}
                      onClick={() => toggleProductSelection(product.id)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        isSelected
                          ? 'bg-emerald-600/20 border-emerald-500'
                          : 'admin-bg-inner border-[var(--admin-border)] hover:border-[var(--admin-border)]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-slate-500'
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-[var(--admin-text)]" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-emerald-500/20' : 'bg-slate-700'
                        }`}>
                          <Package className={`w-5 h-5 ${isSelected ? 'text-emerald-400' : 'admin-text-dim'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[var(--admin-text)] font-medium truncate">{product.name}</p>
                          <div className="flex items-center gap-2 text-xs admin-text-dim">
                            <span>{product.category || 'Uncategorized'}</span>
                            {product.frequency_settings?.frequency && (
                              <>
                                <span>•</span>
                                <span className="capitalize">{product.frequency_settings.frequency.replace('_', ' ')}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="text-center py-8 admin-text-soft">
                  <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No products found</p>
                  <p className="text-xs mt-1">Create products in the Products tab first</p>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-[var(--admin-border)] flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setShowProductModal(false); setSelectedProductIds([]); }} className="border-[var(--admin-border)] admin-text-dim">
                Cancel
              </Button>
              <Button 
                onClick={handleRecommendProduct} 
                disabled={selectedProductIds.length === 0 || recommending}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {recommending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Assign {selectedProductIds.length > 0 ? `${selectedProductIds.length} Product(s)` : 'Products'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>

      {/* Switch to Per-Filing Modal */}
      {showSwitchModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="switch-billing-modal">
          <div className="admin-bg-card-inner border border-[var(--admin-border)] rounded-xl max-w-md w-full p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-[var(--admin-text)] mb-2">Switch to Per-Filing</h3>
            <p className="text-sm admin-text-dim mb-1">This will change billing effective immediately.</p>
            {company?.remaining_filings != null && (
              <p className="text-sm text-amber-400 mb-1">Remaining {company.remaining_filings} annual filings will now require individual payment.</p>
            )}
            <p className="text-xs admin-text-soft mb-4">Any pending prepaid orders will be converted to payment-pending.</p>
            <div className="mb-4">
              <label className="text-xs admin-text-soft mb-1 block">Reason for switch (required)</label>
              <textarea value={switchReason} onChange={(e) => setSwitchReason(e.target.value)}
                placeholder="E.g., Client request — cost savings"
                className="w-full admin-bg-inner border border-[var(--admin-border)] rounded-lg px-3 py-2 text-sm text-[var(--admin-text)] placeholder-slate-600 focus:border-amber-500/50 focus:outline-none resize-none"
                rows={3} data-testid="switch-reason-input" />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSwitchToPerFiling} disabled={!switchReason.trim() || switchingBilling}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-[var(--admin-text)] h-10 text-sm font-semibold" data-testid="switch-confirm-btn">
                {switchingBilling ? 'Switching...' : 'Confirm Switch'}
              </Button>
              <Button variant="outline" onClick={() => { setShowSwitchModal(false); setSwitchReason(''); }}
                className="border-[var(--admin-border)] admin-text-dim h-10 text-sm" data-testid="switch-cancel-btn">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {shAdding && (
        <AdminShareholderModal
          entityCfg={cfg}
          directors={directors || []}
          initial={shPrefill}
          saving={shSaving}
          onSubmit={submitShareholder}
          onClose={() => { setShAdding(false); setShPrefill(null); }}
        />
      )}
    </>
  );
};

// Compact modal for the admin panel to add a shareholder. Pre-fills from a
// director when the "Add as {Shareholder}" button on a director card is used.
const AdminShareholderModal = ({ entityCfg, directors = [], initial, saving, onSubmit, onClose }) => {
  const buildForm = (src) => ({
    shareholder_name: src?.shareholder_name || src?.full_name || '',
    shareholder_email: src?.shareholder_email || src?.email || '',
    phone: src?.phone || '',
    pan_number: src?.pan_number || '',
    aadhaar_number: src?.aadhaar_number || '',
    address: src?.address || '',
    shareholder_type: src?.shareholder_type || 'individual',
    number_of_shares: src?.number_of_shares || 0,
    din_number: src?.din_number || '',
    din_status: src?.din_status || '',
    dsc_status: src?.dsc_status || '',
    dsc_expiry_date: src?.dsc_expiry_date || '',
    father_name: src?.father_name || '',
    date_of_birth: src?.date_of_birth || '',
  });
  const [form, setForm] = useState(buildForm(initial));
  const [copySource, setCopySource] = useState('manual'); // 'manual' or a director id
  const canSave = form.shareholder_name.trim().length > 0;

  const handleFetch = (val) => {
    setCopySource(val);
    if (val === 'manual') {
      setForm(buildForm(null));
      return;
    }
    const d = directors.find(x => x.id === val);
    if (d) setForm(buildForm(d));
  };

  // Strip empty-string values from fields that Pydantic types as `date`,
  // `EmailStr`, or an Enum on the backend. Passing "" for these fails with
  // "Input should be a valid date/email/enum" — omit the key so Optional
  // defaults kick in on the server.
  const buildPayload = () => {
    const p = { ...form };
    ['shareholder_email', 'phone', 'pan_number', 'aadhaar_number', 'din_number',
     'address', 'father_name', 'date_of_birth', 'dsc_expiry_date',
     'dsc_status', 'din_status'].forEach((k) => {
      if (p[k] === '' || p[k] == null) delete p[k];
    });
    return p;
  };

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()} data-testid="admin-add-shareholder-modal">
        <div className="admin-modal-title flex items-center justify-between">
          <span>{`Add ${entityCfg.shareholder_label}`}</span>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {/* Fetch from director — prefill all fields from an existing director, or leave manual */}
        <div className="mt-2 mb-3 p-2.5 rounded border border-emerald-500/30 bg-emerald-500/[0.04]">
          <label className="text-[11px] uppercase tracking-wider text-emerald-400 font-semibold">Start from</label>
          <select
            value={copySource}
            onChange={(e) => handleFetch(e.target.value)}
            className="w-full mt-1 px-2 py-1.5 bg-[#0f1611] border border-emerald-500/40 rounded text-white text-sm focus:outline-none focus:border-emerald-400"
            data-testid="add-sh-source-select"
          >
            <option value="manual">Manually enter details</option>
            {directors.length > 0 && <option disabled>──── Fetch from {entityCfg.director_label.toLowerCase()} ────</option>}
            {directors.map(d => (
              <option key={d.id} value={d.id}>
                {d.full_name}{d.din_number ? ` · DIN ${d.din_number}` : ''}{d.pan_number ? ` · ${d.pan_number}` : ''}
              </option>
            ))}
          </select>
          {copySource !== 'manual' && (
            <p className="text-[11px] text-emerald-300 mt-1">
              Pre-filled from {entityCfg.director_label.toLowerCase()}. Just set the share count below.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <label className="text-xs admin-text-dim">
            Name*
            <input value={form.shareholder_name} onChange={e => setForm({ ...form, shareholder_name: e.target.value })}
              className="w-full mt-1 px-2 py-1.5 bg-[#0f1611] border border-emerald-900/40 rounded text-white text-sm" data-testid="add-sh-name" />
          </label>
          <label className="text-xs admin-text-dim">
            Email
            <input value={form.shareholder_email} onChange={e => setForm({ ...form, shareholder_email: e.target.value })}
              className="w-full mt-1 px-2 py-1.5 bg-[#0f1611] border border-emerald-900/40 rounded text-white text-sm" />
          </label>
          <label className="text-xs admin-text-dim">
            Phone
            <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
              className="w-full mt-1 px-2 py-1.5 bg-[#0f1611] border border-emerald-900/40 rounded text-white text-sm" />
          </label>
          <label className="text-xs admin-text-dim">
            PAN
            <input value={form.pan_number} onChange={e => setForm({ ...form, pan_number: e.target.value.toUpperCase() })}
              className="w-full mt-1 px-2 py-1.5 bg-[#0f1611] border border-emerald-900/40 rounded text-white text-sm" />
          </label>
          <label className="text-xs admin-text-dim">
            Aadhaar
            <input value={form.aadhaar_number} onChange={e => setForm({ ...form, aadhaar_number: e.target.value })}
              className="w-full mt-1 px-2 py-1.5 bg-[#0f1611] border border-emerald-900/40 rounded text-white text-sm" />
          </label>
          <label className="text-xs admin-text-dim">
            Number of shares
            <input type="number" min="0" value={form.number_of_shares}
              onChange={e => setForm({ ...form, number_of_shares: parseInt(e.target.value) || 0 })}
              className="w-full mt-1 px-2 py-1.5 bg-[#0f1611] border border-emerald-900/40 rounded text-white text-sm" data-testid="add-sh-shares" />
          </label>
          <label className="text-xs admin-text-dim sm:col-span-2">
            Address
            <textarea value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
              rows={2}
              className="w-full mt-1 px-2 py-1.5 bg-[#0f1611] border border-emerald-900/40 rounded text-white text-sm resize-none" />
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="admin-btn admin-btn-ghost">Cancel</button>
          <button
            onClick={() => canSave && onSubmit(buildPayload())}
            disabled={saving || !canSave}
            className="admin-btn admin-btn-primary"
            data-testid="add-sh-submit"
          >
            {saving ? 'Saving…' : `Add ${entityCfg.shareholder_label}`}
          </button>
        </div>
      </div>
    </div>
  );
};

const GST_STATE_MAP = {"01":"Jammu & Kashmir","02":"Himachal Pradesh","03":"Punjab","04":"Chandigarh","05":"Uttarakhand","06":"Haryana","07":"Delhi","08":"Rajasthan","09":"Uttar Pradesh","10":"Bihar","11":"Sikkim","12":"Arunachal Pradesh","13":"Nagaland","14":"Manipur","15":"Mizoram","16":"Tripura","17":"Meghalaya","18":"Assam","19":"West Bengal","20":"Jharkhand","21":"Odisha","22":"Chhattisgarh","23":"Madhya Pradesh","24":"Gujarat","27":"Maharashtra","29":"Karnataka","30":"Goa","32":"Kerala","33":"Tamil Nadu","36":"Telangana","37":"Andhra Pradesh"};
const getGstStateName = (gstin) => {
  if (!gstin || gstin.length < 2) return '';
  return GST_STATE_MAP[gstin.substring(0, 2)] || gstin.substring(0, 2);
};


export const CompaniesTab = ({ companies, loading, onSelectCompany, selectedCompany, directors, shareholders, orders, owner, onSelectDirector, onSelectShareholder, onSelectOrder, onSelectOwner, onBack, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCompanies = companies.filter(company => {
    return company.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.cin_llpin?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Companies List */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 admin-text-soft" />
            <Input 
              placeholder="Search companies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 admin-bg-card-inner border-[var(--admin-border)] text-[var(--admin-text)] placeholder:admin-text-soft focus:border-[var(--admin-green)]"
            />
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="admin-card p-5 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-slate-700"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-slate-700 rounded w-40 mb-2"></div>
                    <div className="h-3 bg-slate-700 rounded w-24"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto pr-2">
            {filteredCompanies.map((company) => (
              <CompanyCard 
                key={company.id} 
                company={company} 
                onClick={() => onSelectCompany(company)}
                isSelected={selectedCompany?.id === company.id}
              />
            ))}
            {filteredCompanies.length === 0 && (
              <div className="text-center py-8 admin-text-soft">
                {searchTerm ? `No companies found matching "${searchTerm}"` : 'No companies registered'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Company Details */}
      <CompanyDetailPanel 
        company={selectedCompany} 
        directors={directors}
        shareholders={shareholders}
        orders={orders}
        owner={owner}
        onSelectDirector={onSelectDirector}
        onSelectShareholder={onSelectShareholder}
        onSelectOrder={onSelectOrder}
        onSelectOwner={onSelectOwner}
        onBack={onBack}
        onRefresh={onRefresh}
      />

    </div>
  );
};
